import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

const API = 'https://tribaltech-backend.onrender.com';

function dollars(cents) { return '$' + (cents / 100).toFixed(2); }

export default function CreatorStorefront() {
  const { username } = useParams();
  const [params] = useSearchParams();
  const [creator, setCreator]   = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState('');
  const [subEmail, setSubEmail]   = useState('');
  const [subBusy, setSubBusy]     = useState(false);
  // Are we signed in as a subscriber to this creator?
  const [isSubscriber, setIsSubscriber] = useState(false);

  useEffect(() => {
    const ref = params.get('ref');
    if (ref) {
      sessionStorage.setItem('vault_ref', ref.toUpperCase());
      axios.post(`${API}/api/affiliate/track-click`, { code: ref.toUpperCase() }).catch(() => {});
    }
  }, [params]);

  // Handle Stripe success redirect after subscribe — exchange the
  // session_id for a vault JWT so the fan lands on the page
  // already signed in and sees unlocked state.
  useEffect(() => {
    if (params.get('subscribed') !== '1') return;
    const sid = params.get('session_id');
    if (!sid) return;
    axios.post(`${API}/api/subscriptions/claim`, { session_id: sid })
      .then(r => {
        localStorage.setItem('vault_token', r.data.jwt);
        localStorage.setItem('vault_email', r.data.email);
      })
      .catch(e => console.warn('[storefront] subscription claim failed:', e.message));
  }, [params]);

  useEffect(() => {
    axios.get(`${API}/api/storefront/${encodeURIComponent(username)}`)
      .then(r => { setCreator(r.data.creator); setListings(r.data.listings || []); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [username]);

  // Check vault-JWT → do we have an active sub to this creator?
  // Hits /api/subscriptions/mine which is cheap and includes
  // creator_id, so we just filter.
  useEffect(() => {
    if (!creator) return;
    const token = localStorage.getItem('vault_token');
    if (!token) { setIsSubscriber(false); return; }
    axios.get(`${API}/api/subscriptions/mine`, {
      headers: { Authorization: 'Bearer ' + token },
    }).then(r => {
      const match = (r.data.subscriptions || []).find(s =>
        s.creator_id === creator.id && ['active', 'past_due'].includes(s.status)
      );
      setIsSubscriber(!!match);
    }).catch(() => setIsSubscriber(false));
  }, [creator]);

  async function subscribe(e) {
    e?.preventDefault();
    const email = (subEmail || localStorage.getItem('vault_email') || '').trim();
    if (!email) { alert('Enter your email.'); return; }
    setSubBusy(true);
    try {
      const r = await axios.post(`${API}/api/subscriptions/subscribe`, {
        creator_id: creator.id,
        email,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      throw new Error('no checkout URL');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
      setSubBusy(false);
    }
  }

  async function openSubscriberStream(listingId) {
    const token = localStorage.getItem('vault_token');
    try {
      const r = await axios.get(`${API}/api/vault/listings/${listingId}/stream`, {
        params: { mode: 'watch' },
        headers: { Authorization: 'Bearer ' + token },
      });
      if (r.data?.url) window.open(r.data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  }

  const subPriceDollars = creator?.subscription_price_cents
    ? (creator.subscription_price_cents / 100).toFixed(2) : null;

  return (
    <div style={page}>
      <div style={{ maxWidth: 1100, margin:'0 auto', padding:'40px 20px' }}>
        <div style={{ marginBottom: 30 }}>
          <div style={eyebrow}>Creator Protect · Storefront</div>
          <h1 style={{ color:'#fff', fontSize: 32, margin:'4px 0 0' }}>@{creator?.username || username}</h1>
          {isSubscriber && (
            <div style={{ marginTop: 10, display:'inline-block', padding:'4px 12px', background:'rgba(74,222,128,0.12)', border:'1px solid rgba(74,222,128,0.3)', color:'#4ade80', fontSize: 12, borderRadius: 6 }}>
              ✓ Subscribed — all content unlocked
            </div>
          )}
        </div>

        {!isSubscriber && creator?.subscription_enabled && subPriceDollars && (
          <div style={subCard}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color:'#a78bfa', textTransform:'uppercase', letterSpacing: 2, marginBottom: 6 }}>
                Monthly subscription
              </div>
              <div style={{ fontSize: 22, color:'#fff', fontWeight: 600, marginBottom: 6 }}>
                Unlock everything for ${subPriceDollars}/mo
              </div>
              <div style={{ fontSize: 13, color:'#94a3b8' }}>
                Access every video on this storefront. Cancel anytime. One-time purchases still available below.
              </div>
            </div>
            <form onSubmit={subscribe} style={{ display:'flex', gap: 8, alignItems:'center' }}>
              <input
                type="email"
                value={subEmail}
                onChange={e => setSubEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={subBusy}
                style={{ padding: 10, borderRadius: 8, background:'#0a0a0a', border:'1px solid #222', color:'#fff', fontSize: 13, width: 200 }}
              />
              <button type="submit" disabled={subBusy} style={{ padding:'10px 18px', borderRadius: 8, border:'none', background:'#7c3aed', color:'#fff', fontSize: 13, fontWeight: 600, cursor:'pointer' }}>
                {subBusy ? '…' : `Subscribe $${subPriceDollars}/mo`}
              </button>
            </form>
          </div>
        )}

        {loading && <div style={{ color:'#888' }}>Loading…</div>}
        {err && <div style={{ color:'#f87171' }}>{err}</div>}

        {!loading && !err && listings.length === 0 && (
          <div style={emptyBox}>
            This creator hasn't published any videos yet.
          </div>
        )}

        <div style={grid}>
          {listings.map(l => (
            <div key={l.id} style={card}>
              <Link to={`/ppv/${l.slug || l.id}`} style={{ textDecoration:'none', display:'block' }}>
                <div style={thumbWrap}>
                  {l.preview_url ? (
                    <video src={l.preview_url} muted playsInline preload="metadata" style={thumb} />
                  ) : (
                    <div style={{ ...thumb, display:'flex', alignItems:'center', justifyContent:'center', color:'#555' }}>
                      No preview
                    </div>
                  )}
                  {isSubscriber && (
                    <div style={{ position:'absolute', top: 8, left: 8, padding:'3px 8px', background:'rgba(74,222,128,0.85)', color:'#0a0a0a', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, borderRadius: 4 }}>
                      UNLOCKED
                    </div>
                  )}
                </div>
              </Link>
              <div style={{ padding: 16 }}>
                <div style={{ color:'#fff', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{l.title}</div>
                {l.description && (
                  <div style={{ color:'#94a3b8', fontSize: 12, marginBottom: 10, height: 32, overflow:'hidden' }}>
                    {l.description}
                  </div>
                )}
                {isSubscriber ? (
                  <button onClick={() => openSubscriberStream(l.id)}
                          style={{ width:'100%', padding:'8px 12px', borderRadius: 6, border:'none', background:'#4ade80', color:'#0a0a0a', fontSize: 13, fontWeight: 600, cursor:'pointer' }}>
                    Watch
                  </button>
                ) : (
                  <div style={{ color:'#a78bfa', fontSize: 16, fontWeight: 600 }}>{dollars(l.price_cents)}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 40, fontSize: 11, color:'#555', textAlign:'center' }}>
          Already a buyer or subscriber? <Link to="/vault/login" style={{ color:'#a78bfa' }}>Open your vault →</Link>
        </div>
      </div>
    </div>
  );
}

const page = { minHeight:'100vh', background:'linear-gradient(180deg, #0f0f1e 0%, #0a0a0f 100%)' };
const eyebrow = { fontSize: 11, color:'#888', letterSpacing: 2, textTransform:'uppercase' };
const subCard = {
  background:'#111', borderRadius: 12, border:'1px solid rgba(124,58,237,0.35)', padding: 20,
  marginBottom: 30, display:'flex', alignItems:'center', gap: 20, flexWrap:'wrap',
};
const grid = {
  display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap: 20,
};
const card = {
  background:'#111', borderRadius: 12, border:'1px solid #222', overflow:'hidden',
};
const thumbWrap = { aspectRatio:'16/9', background:'#0a0a0a', position:'relative', cursor:'pointer' };
const thumb     = { width:'100%', height:'100%', objectFit:'cover', display:'block' };
const emptyBox = {
  background:'#111', borderRadius: 12, border:'1px solid #222',
  padding: 40, color:'#94a3b8', textAlign:'center', lineHeight: 1.6,
};
